import { useRef, useCallback } from 'react';
import html2canvas from 'html2canvas';

interface UseRiskMeterExportResult {
    containerRef: React.RefObject<HTMLDivElement>;
    exportAsPng: () => Promise<void>;
}

export function useRiskMeterExport(title?: string, filename?: string): UseRiskMeterExportResult {
    const containerRef = useRef<HTMLDivElement>(null);

    const exportAsPng = useCallback(async () => {
        const element = containerRef.current;
        if (!element) return;

        const scale = 2;
        const titleHeight = title ? 60 : 0;

        // Capture the container
        const captured = await html2canvas(element, {
            scale,
            backgroundColor: '#ffffff',
            useCORS: true
        });

        // Create final canvas with space for title
        const finalCanvas = document.createElement('canvas');
        finalCanvas.width = captured.width;
        finalCanvas.height = captured.height + (titleHeight * scale);
        const ctx = finalCanvas.getContext('2d');
        if (!ctx) return;

        // White background
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, finalCanvas.width, finalCanvas.height);

        // Draw title if provided
        if (title) {
            ctx.fillStyle = '#333333';
            ctx.font = `bold ${18 * scale}px Segoe UI, Arial, sans-serif`;
            ctx.textAlign = 'center';
            ctx.fillText(title, finalCanvas.width / 2, 38 * scale);
        }

        // Draw captured content below title
        ctx.drawImage(captured, 0, titleHeight * scale);

        // Download via anchor-click pattern
        const dataUrl = finalCanvas.toDataURL('image/png');
        const link = document.createElement('a');
        link.download = filename ?? 'RiskMeter.png';
        link.href = dataUrl;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }, [title, filename]);

    return { containerRef, exportAsPng };
}
